import { useRef, useState } from 'react';

import { Gauge, Coxcomb, GapChart } from './charts.jsx';
import { Readiness, AiReadiness, Dii } from './panels.jsx';

/**
 * The respondent's report — a port of app/public/dashboard.js `renderReport`.
 *
 * One shared hover index links the coxcomb and the gap chart (spec §5). Only
 * those two read it; the gauge and the bottom panels never react to hover.
 *
 * Both tab panes are rendered once and toggled with `hidden`, as legacy did.
 * Note what that does and does not buy: it keeps state and avoids rebuilding
 * the charts, but returning to the maturity tab STILL replays its entry
 * animations, in both frontends -- `hidden` is display:none, and CSS
 * animations restart when an element is shown again. The legacy comment
 * claimed otherwise. Behaviour is kept as legacy's; stopping the replay
 * would be a design decision, not a port.
 */
export default function Report({ report, benchmark, children }) {
  const [hover, setHover] = useState(null);
  const [tab, setTab] = useState('maturity');
  const container = useRef(null);

  const dii = <Dii report={report} benchmark={benchmark} />;
  const hasDii = Boolean(report.dii);

  const maturityPane = (
    <div className="report-pane" hidden={hasDii && tab !== 'maturity'}>
      <div className="row-card">
        <Gauge report={report} benchmark={benchmark} />
        <Coxcomb report={report} hover={hover} onHover={setHover} />
        <GapChart report={report} benchmark={benchmark} hover={hover} onHover={setHover} />
      </div>
      <div className="panels-row">
        <Readiness report={report} />
        <AiReadiness report={report} benchmark={benchmark} />
      </div>
    </div>
  );

  // Without a DII the maturity pane is the whole report: no tabs, and an
  // empty DII pane left in place exactly as the vanilla renderer left it.
  const diiPane = <div className="report-pane" hidden={hasDii && tab !== 'dii'}>{hasDii && dii}</div>;

  const select = (id) => {
    setTab(id);
    container.current?.scrollIntoView?.({ block: 'start' });
  };

  const panes = [
    { id: 'maturity', label: 'Digital & AI Maturity' },
    { id: 'dii', label: 'Digital Intensity Index' },
  ];

  return (
    <div id="report" className="report-container" ref={container}>
      {/* Staff drill-through puts its return bar here (Step 4). */}
      {children}

      {/* The company owns the page; the tabs below name the two sections. */}
      <div className="title-block">
        <div className="eyebrow">Your report</div>
        <h1 className="report-h1">{report.company.companyName}</h1>
        <p className="report-sub">{[report.company.county, report.company.subSector].filter(Boolean).join(' · ')}</p>
      </div>

      {hasDii && (
        <div className="admin-tabs report-tabs" role="tablist">
          {panes.map((pane) => (
            <button
              key={pane.id}
              type="button"
              className={`admin-tab${tab === pane.id ? ' active' : ''}`}
              role="tab"
              aria-selected={String(tab === pane.id)}
              onClick={() => select(pane.id)}
            >
              {pane.label}
            </button>
          ))}
        </div>
      )}

      {maturityPane}
      {diiPane}
    </div>
  );
}
